import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { ApiError, fetchMe, login as apiLogin, type LoginResult } from "./api";
import { markSsoSignedOut, redirectToSsoLogout, ssoLogoutSameSite } from "./sso-client";

interface AuthData {
  token: string | null;
  username: string | null;
  displayName: string | null;
  roles: string[];
  ready: boolean; // hydrated from storage
}

interface AuthState extends AuthData {
  signIn: (username: string, password: string) => Promise<void>;
  signInWithToken: (result: LoginResult) => void;
  updateToken: (token: string) => void;
  signOut: () => Promise<void>;
}

const EMPTY: AuthData = { token: null, username: null, displayName: null, roles: [], ready: false };
// Deliberately NOT "icm_auth": the store app may share this origin during dev, and the
// two sessions must not clobber each other.
const STORAGE_KEY = "icm_payreq_auth";
const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthData>(EMPTY);

  useEffect(() => {
    let next: AuthData = { ...EMPTY, ready: true };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        next = {
          token: s.token ?? null,
          username: s.username ?? null,
          displayName: s.displayName ?? null,
          roles: s.roles ?? [],
          ready: true,
        };
      }
    } catch {
      // ignore corrupt storage
    }
    setAuth(next);
  }, []);

  // Durability: ask the browser to keep our storage persistent so the saved session (the JWT in
  // localStorage) isn't evicted under storage pressure / privacy clean-up — which would force a
  // re-login. Nothing happens if the API is unavailable.
  useEffect(() => {
    const sm = typeof navigator !== "undefined" ? navigator.storage : undefined;
    if (!sm?.persist) return;
    sm.persisted()
      .then((already) => {
        if (!already) void sm.persist();
      })
      .catch(() => {});
  }, []);

  // Refresh the profile from the backend whenever we hold a token (after login + on reload).
  // Doubles as a token check: a 401 means the stored token is stale → sign out.
  useEffect(() => {
    if (!auth.ready || !auth.token) return;
    const token = auth.token;
    let cancel = false;
    fetchMe(token)
      .then((me) => {
        if (cancel) return;
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ token, username: me.username, displayName: me.displayName, roles: me.roles })
        );
        setAuth((a) =>
          a.token === token
            ? {
                ...a,
                username: me.username ?? a.username,
                displayName: me.displayName ?? a.displayName,
                roles: me.roles?.length ? me.roles : a.roles,
              }
            : a
        );
      })
      .catch((e) => {
        if (!cancel && e instanceof ApiError && e.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setAuth({ ...EMPTY, ready: true });
        }
      });
    return () => {
      cancel = true;
    };
  }, [auth.ready, auth.token]);

  function signInWithToken(r: LoginResult) {
    setAuth({
      token: r.token,
      username: r.username,
      displayName: r.displayName,
      roles: r.roles,
      ready: true,
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(r));
  }

  // Swap in a re-minted token while keeping the current identity. The profile-refresh
  // effect re-validates it against /api/me.
  function updateToken(newToken: string) {
    setAuth((a) => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ token: newToken, username: a.username, displayName: a.displayName, roles: a.roles })
        );
      } catch {
        // ignore
      }
      return { ...a, token: newToken };
    });
  }

  async function signIn(username: string, password: string) {
    signInWithToken(await apiLogin(username, password));
  }

  /**
   * Sign out of this app AND the org-wide ICM session, landing on a clean /login.
   * Callers do not navigate themselves: this owns the whole sequence.
   */
  async function signOut() {
    // Mark the deliberate sign-out FIRST, before any route guard reacts: it stops the login
    // page silently signing the user straight back in with a still-live org session.
    markSsoSignedOut();
    setAuth({ ...EMPTY, ready: true });
    localStorage.removeItem(STORAGE_KEY);
    // End the ORG-WIDE session. Served from *.rgseva.com we can revoke it with a background
    // call and stay in THIS window, which is what makes sign-out work in an installed PWA: there,
    // handing the browser to the out-of-scope login.rgseva.com opens a Custom Tab over this page
    // rather than navigating it, so the app was left sitting on its signed-in screen.
    if (await ssoLogoutSameSite()) {
      window.location.replace("/login");
      return;
    }
    // Off-site, the cookie only ever travels on a top-level navigation, so hand the whole
    // browser to login.rgseva.com: it revokes the session same-origin and returns here.
    if (redirectToSsoLogout(`${window.location.origin}/login`)) return;
    // No SSO configured → straight to the login page, as a full load so no stale state survives.
    window.location.replace("/login");
  }

  return (
    <AuthContext.Provider value={{ ...auth, signIn, signInWithToken, updateToken, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
